import { SetStateAction, Dispatch } from "react";
import { captureMessage, withScope } from "@/observability/sentry";
import { isAxiosError } from "axios";
import { isObjKey } from "./typeCheckUtils";

type ErrorCodeMessage = {
  code: string;
  message: string;
};

export const RATE_LIMIT_ERROR_MESSAGE =
  "Too many attempts. Please wait a moment and try again.";

export function isRateLimitError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 429;
}

// Translates backend messages that leak raw ORM field names (e.g.
// `timestamp__lte must be after timestamp__gte`) into copy a user can act on.
export function getFriendlyBackendMessage(message: string): string {
  if (/timestamp__lte\s+must\s+be\s+after\s+timestamp__gte/i.test(message)) {
    return "End date must be after start date.";
  }
  return message;
}

export type IErrorData =
  | {
      [key: string]: ErrorCodeMessage[];
    }
  | ErrorCodeMessage[];

// Backend (DRF) validation errors are not always the flat
// `{ field: [{ code, message }] }` shape. A nested serializer returns nested
// objects, e.g. replacing proxies with an invalid IP yields
// `{ to_replace: { ip_addresses: { 0: [{ code: "invalid", message: "Enter a valid IPv4 address." }] } } }`.
// Recursively collect every leaf `message` so we can surface it to the user
// instead of dropping it (and capturing "Error should be present" in Sentry).
export function flattenErrorMessages(value: unknown): string[] {
  if (!value) return [];
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(flattenErrorMessages);
  if (typeof value === "object") {
    const { message } = value as { message?: unknown };
    if (typeof message === "string") return [message];
    return Object.values(value as Record<string, unknown>).flatMap(
      flattenErrorMessages
    );
  }
  return [];
}

interface IProps {
  error: unknown;
  errorHandlers?: IErrorHandlers;
  setSnackbarErrorMessage?: (value: string) => void;
}

interface IErrorErrorCodeHandlers {
  [key: string]: Dispatch<SetStateAction<string | null>>;
}

export interface IErrorHandlers {
  [key: string]:
    | Dispatch<SetStateAction<string | null>>
    | IErrorErrorCodeHandlers
    | ((param: string) => void);
}

export function logAndPrintWhenNoErrorHandler(
  errorData: IErrorData,
  setSnackbarErrorMessage?: (value: string) => void
) {
  const errorMessageList: string[] = [];
  const keyList: string[] = [];

  (Array.isArray(errorData)
    ? errorData.map((entry) => [entry.code, [entry]] as const)
    : Object.entries(errorData)
  ).forEach(([key, errorArray]) => {
    // Flattened because a list serializer nests its errors per item, e.g. the
    // proxy tester's `{ proxies: [{ target_url: [{ code, message }] }] }`.
    // Reading `message` one level down joined the undefineds into " ", which
    // opened an empty snackbar.
    errorMessageList.push(...flattenErrorMessages(errorArray));
    keyList.push(key);
  });

  //Snackbar should handle long cases.
  const cumErrorMessage = [...new Set(errorMessageList)].join(" ");
  if (setSnackbarErrorMessage && cumErrorMessage) {
    setSnackbarErrorMessage(cumErrorMessage);
  }

  if (!cumErrorMessage || !setSnackbarErrorMessage) {
    withScope((scope) => {
      scope.setLevel("error");
      scope.setExtra("errorHandlers", "None");
      scope.setExtra("keylist", JSON.stringify(keyList));
      scope.setExtra("errorData", JSON.stringify(errorData));
      scope.setExtra("errorMessage", cumErrorMessage);
      captureMessage(`No errors are handled ${JSON.stringify(keyList)}`);
    });
  }
}

export const setUiErrors = ({
  errorData,
  errorHandlers,
  setSnackbarErrorMessage,
}: {
  errorData: IErrorData;
  errorHandlers: IErrorHandlers | undefined;
  setSnackbarErrorMessage?: (value: string) => void;
}) => {
  //Always log and print error when no errorHandler is provided.
  if (!errorHandlers) {
    logAndPrintWhenNoErrorHandler(errorData, setSnackbarErrorMessage);
    return;
  }

  (Array.isArray(errorData)
    ? errorData.map((entry) => [entry.code, [entry]] as const)
    : Object.entries(errorData)
  ).forEach(([key, errorArray]) => {
    // Use the flattened messages so deeply-nested DRF errors (e.g.
    // `to_replace.ip_addresses.0`) still surface to the user instead of being
    // dropped and reported to Sentry as "Error should be present".
    const flattenedMessages = flattenErrorMessages(errorArray);
    const errorMessages = flattenedMessages.length
      ? flattenedMessages.join(", ")
      : null;

    if (!errorMessages) {
      withScope((scope) => {
        scope.setLevel("error");
        scope.setExtra("errorArray", errorArray);
        scope.setExtra("errorData", JSON.stringify(errorData));
        captureMessage("Error should be present");
      });
    }

    if (isObjKey(key, errorHandlers)) {
      // Case errorHandlers are error code based.
      if (typeof errorHandlers[key] === "object") {
        // Loop through error codes and call setters.
        errorArray.forEach(({ code, message }) => {
          if (isObjKey(code, errorHandlers[key])) {
            (errorHandlers[key] as IErrorErrorCodeHandlers)[code](message);
          }
          // Log if error code is not handled for error
          else {
            //Print error on Drawer Error Snackbar. No code specified for this error.
            if (setSnackbarErrorMessage) {
              setSnackbarErrorMessage(message);
            }
          }
        });
      }
      // Case errorHandlers are message based.
      else {
        (errorHandlers[key] as Dispatch<SetStateAction<string | null>>)(
          errorMessages
        );
      }
    } else {
      if (setSnackbarErrorMessage) {
        if (errorMessages) {
          setSnackbarErrorMessage(errorMessages);
        } else {
          withScope((scope) => {
            scope.setLevel("error");
            scope.setExtra("errorData", JSON.stringify(errorData));
            captureMessage(
              "a field is present on the server that is not handled in ui"
            );
          });
        }
      } else {
        withScope((scope) => {
          scope.setLevel("error");
          scope.setExtra("errorData", JSON.stringify(errorData));
          captureMessage("setSnackbarErrorMessage does not exist");
        });
      }
    }
  });
};

// Failures without field-level error data (network problems, non-axios
// throws, server-side throttling) map straight to snackbar copy; `null` means
// the error may carry field errors and needs the 400 handling below.
function getTransportErrorMessage(error: unknown): string | null {
  const err = (error ?? {}) as {
    type?: string;
    name?: string;
    code?: string;
  };
  if (err.type === "fetch_error" && err.name === "FetchError") {
    return "Something went wrong please try again";
  }
  if (err.code === "ECONNABORTED") {
    return "Connection timed out please try again";
  }
  if (!isAxiosError(error)) {
    return "Something went wrong please try again";
  }
  // Server-side throttling (e.g. repeated signup or purchase attempts) has no
  // field-level error data — surface it instead of silently dropping it.
  if (error.response?.status === 429) {
    return RATE_LIMIT_ERROR_MESSAGE;
  }
  return null;
}

export const handleApiError = ({
  error,
  errorHandlers,
  setSnackbarErrorMessage,
}: IProps) => {
  const transportMessage = getTransportErrorMessage(error);
  if (transportMessage) {
    if (setSnackbarErrorMessage) {
      setSnackbarErrorMessage(transportMessage);
    }
    return;
  }

  if (!isAxiosError<IErrorData>(error) || error.response?.status !== 400) {
    return;
  }

  const errorData = error.response?.data;

  if (!errorData || !Object.keys(errorData).length) {
    return;
  }

  setUiErrors({ errorData, errorHandlers, setSnackbarErrorMessage });
};
